"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const Number_1 = require("@pups/utility/build/Number");
const Object_1 = require("@pups/utility/build/Object");
const ColorBase_1 = require("./ColorBase");
/**
 * Classe per la gestione di un colore
 *
 * @class Color
 * @extends {ColorBase}
 */
class Color extends ColorBase_1.default {
    constructor(colorStringOrObject, name = '', tags = [], id, defaultFormat) {
        super(colorStringOrObject, name, tags, id, defaultFormat);
        let RGB = this.value.getRgb();
        this.brightness = (RGB.r * 299 + RGB.g * 587 + RGB.b * 114) / 1000;
        this.isDark = this.brightness < 128;
        this.isLight = !this.isDark;
        RGB = Object_1.map(RGB, (v) => (v / 255) <= 0.03928 ? (v / 255) / 12.92 : Math.pow((((v / 255) + 0.055) / 1.055), 2.4));
        this.luminance = (0.2126 * RGB.r) + (0.7152 * RGB.g) + (0.0722 * RGB.b);
    }
    /**
     * Create a new color
     *
     * @private
     * @param {Types.Color.All} value
     * @param {string} name
     * @returns
     * @memberof Color
     */
    create(value, name) {
        return new Color(value, name, this.getTags(), null, this.format);
    }
    /**
     * Setta l'alpha del colore
     *
     * @param {number} alpha
     * @param {string} [name]
     * @returns {Color}
     * @memberof Color
     */
    alpha(alpha, name) {
        const rgb = this.value.getRgb();
        rgb.a = alpha;
        return this.create(rgb, name ? name : this.getName() + '--alpha-' + alpha * 100);
    }
    /**
     * Desatura un colore
     *
     * @param {number} [amount=10]
     * @param {string} [name]
     * @returns {Color}
     * @memberof Color
     */
    desaturate(amount = 10, name) {
        const hsl = this.value.getHsl();
        hsl.s = Number_1.clamp01(hsl.s - amount / 100);
        return this.create(hsl, name ? name : this.getName() + '--desaturate-' + amount);
    }
    /**
     * Satura un colore
     *
     * @param {number} [amount=10]
     * @returns {Color}
     * @memberof Color
     */
    saturate(amount = 10) {
        const hsl = this.value.getHsl();
        hsl.s = Number_1.clamp01(hsl.s + amount / 100);
        return this.create(hsl, this.getName() + '--saturate-' + amount);
    }
    /**
     * Colore in scala di grigi
     *
     * @returns {Color}
     * @memberof Color
     */
    greyscale() {
        return this.desaturate(100, this.getName() + '--greyscale');
    }
    /**
     * Aggiunge luminosità ad un colore
     *
     * @param {number} [amount=10]
     * @returns {Color}
     * @memberof Color
     */
    lighten(amount = 10) {
        let hsl = this.value.getHsl();
        hsl.l = Number_1.clamp01(hsl.l + amount / 100);
        return this.create(hsl, this.getName() + '--lighten-' + amount);
    }
    /**
     * Rende il colore più chiaro, tendende al bianco
     *
     * @param {number} [amount=10]
     * @returns {Color}
     * @memberof Color
     */
    brighten(amount = 10) {
        const rgb = Object_1.map(this.value.getRgb(), v => Number_1.clamp(0, 255, v - Math.round(255 * -(amount / 100))));
        return this.create(rgb, this.getName() + '--brighten-' + amount);
    }
    /**
     * Diminuisce la luminosità del colore
     *
     * @param {number} [amount=10]
     * @returns {Color}
     * @memberof Color
     */
    darken(amount = 10) {
        const hsl = this.value.getHsl();
        hsl.l = Number_1.clamp01(hsl.l - amount / 100);
        return this.create(hsl, this.getName() + '--darken-' + amount);
    }
    /**
     * Modifica l'Hue del colore
     *
     * @param {number} amount
     * @param {string} [name]
     * @returns {Color}
     * @memberof Color
     */
    spin(amount, name) {
        const hsl = this.value.getHsl();
        hsl.h *= 360;
        hsl.h = (hsl.h + amount) % 360;
        hsl.h = (hsl.h < 0 ? 360 + hsl.h : hsl.h) / 360;
        return this.create(hsl, name ? name : this.getName() + '--spin-' + amount);
    }
    /**
     * Ritorna il colore complementare
     *
     * @returns {Color}
     * @memberof Color
     */
    complement() {
        return this.spin(180, this.getName() + '--complement');
    }
    /**
     * Ritorna la triade del colore
     *
     * @returns {Array<Color>}
     * @memberof Color
     */
    triad() {
        return [
            this,
            this.spin(120, this.getName() + '--traid-120'),
            this.spin(240, this.getName() + '--traid-240')
        ];
    }
    /**
     * RItorna la tetrade del colore
     *
     * @returns {Array<Color>}
     * @memberof Color
     */
    tetrad() {
        return [
            this,
            this.spin(90, this.getName() + '--traid-90'),
            this.spin(180, this.getName() + '--traid-180'),
            this.spin(270, this.getName() + '--traid-270')
        ];
    }
    /**
     * Ritorna i complementari
     *
     * @returns {Array<Color>}
     * @memberof Color
     */
    splitcomplement() {
        return [
            this,
            this.spin(72, this.getName() + '--complment-1'),
            this.spin(216, this.getName() + '--complment-2')
        ];
    }
    /**
     * Colori analoghi
     *
     * @param {number} [results=6]
     * @param {number} [slices=30]
     * @returns {Array<Color>}
     * @memberof Color
     */
    analogous(results = 6, slices = 30) {
        const hsl = this.value.getHsl();
        const part = 360 / slices;
        const ret = [];
        for (hsl.h = ((hsl.h - (part * results >> 1)) + 720) % 360; --results;) {
            hsl.h = (hsl.h + part) % 360;
            ret.push(this.create(Object.assign(Object.assign({}, hsl), { h: hsl.h / 360 }), this.getName() + '--analogous-' + results));
        }
        return ret.sort((a, b) => a.brightness > b.brightness ? -1 : 1);
    }
    /**
     * Ritorna la scala di luminosità del colore
     *
     * @param {number} [results=6]
     * @returns {Array<Color>}
     * @memberof Color
     */
    monochromatic(results = 6) {
        const hsv = this.value.getHsv();
        const ret = [];
        const modification = 1 / results;
        while (results--) {
            ret.push(this.create(Object.assign(Object.assign({}, hsv), { v: hsv.v }), this.getName() + '--monochromatic-' + results));
            hsv.v = (hsv.v + modification) % 1;
        }
        return ret.sort((a, b) => a.luminance > b.luminance ? 1 : -1);
    }
}
exports.default = Color;
//# sourceMappingURL=Color.js.map